import { DOCUMENT } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '../../core/logging.service';
import { ApiService } from '../../core/api.service';
import { ProviderName } from '../../../../account/models/login-response.model';
import { ExpressCheckoutCallback } from '../../../models/express-checkout/express-checkout-endpoints.model';
import { ExpressCheckoutContext } from '../../../models/orders/express-checkout.token';
import { ProviderUiActionResponse } from '../../../../ecash/models/provider-ui-action-response.model';
import { GiftCodeModel } from '../../../../purchase/models/giftcode/gift-code.model';

/** Options controlling how an external script tag is created. */
export interface LoadScriptOptions {
  /** Load asynchronously (default true). */
  async?: boolean;
  /** Defer execution until after parsing (default false). */
  defer?: boolean;
  /** crossorigin attribute, e.g. 'anonymous'. */
  crossOrigin?: string;
  /** CSP nonce to stamp on the tag. */
  nonce?: string;
  /** Reject if the script has not loaded within this many ms (0 / omitted disables the timeout). */
  timeoutMs?: number;
}

/** Internal record tracking a loaded (or in-flight) external script. */
interface ScriptRecord {
  src: string;
  el: HTMLScriptElement;
  promise: Promise<HTMLScriptElement>;
  loaded: boolean;
}

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Dedicated service for express checkout concerns, independent of the standard PaymentService.
 * Owns loading of third-party express wallet SDK scripts (e.g. Paze).
 *
 * Guarantees:
 *  - de-duplication: one <script> per url, concurrent callers share a single in-flight promise;
 *  - adoption: an existing matching tag in the DOM is reused instead of duplicated;
 *  - robust failure: load errors and timeouts reject with a real Error and clean up state;
 *  - configurable: async/defer/crossorigin/nonce for CSP-constrained environments.
 */
@Injectable({
  providedIn: 'root',
})
export class ExpressCheckoutService {
  private logService = inject(LoggingService);
  private readonly document = inject(DOCUMENT);
  private api = inject(ApiService);

  private readonly scripts = new Map<string, ScriptRecord>();

  /* ================================
   * Express Checkout API (spec: api/cardtokens/express/*, api/checkout/express/* and
   * api/register/express/*)
   * ================================ */

  /**
   * Start an express checkout session, routing to the card-token or marketplace init endpoint based
   * on `context.mode`. Prefer this over the two mode-specific methods so callers pass one context
   * and let the service pick the endpoint. Defaults to the card-token flow when no mode is set.
   */
  initExpress(
    context: ExpressCheckoutContext | undefined,
    providerId: ProviderName,
    providerSubId?: ProviderName
  ): Promise<ProviderUiActionResponse> {
    switch (context?.mode) {
      case 'marketplace':
        return this.initCheckoutExpress(context.orderId, providerId, {
          providerSubId,
          saveCardToken: context.saveCardToken,
        });
      case 'registration':
        return this.initRegistrationExpress(context.basketItems ?? [], providerId, {
          providerSubId,
          useCardToken: context.useCardToken,
        });
      default:
        return this.initCardTokenExpress(providerId, providerSubId);
    }
  }

  /**
   * Start an express checkout session for the current registration's basket. Unlike the checkout
   * init above, this has no order yet, so the basket items are posted as the body.
   * POST api/register/express/init?providerId=&providerSubId=&useCardToken=
   */
  initRegistrationExpress(
    basketItems: unknown[],
    providerId: ProviderName,
    options: { providerSubId?: ProviderName; useCardToken?: boolean } = {}
  ): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('providerId', providerId);
    if (options.providerSubId != null) params = params.set('providerSubId', options.providerSubId);
    if (options.useCardToken != null) params = params.set('useCardToken', options.useCardToken);
    return firstValueFrom(this.api.post<any>('register/express/init', basketItems, params));
  }

  /**
   * Complete an express registration payment. The server rebuilds the basket, completes the payment,
   * then saves the registration against the resulting order id.
   * POST api/register/express/complete?orderId=&providerId=&providerSubId=&useCardToken=
   */
  completeRegistrationExpress(
    orderId: string,
    providerId: ProviderName,
    request: { giftCodes: GiftCodeModel[]; callback: ExpressCheckoutCallback },
    options: { providerSubId?: ProviderName; useCardToken?: boolean } = {}
  ): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('orderId', orderId).set('providerId', providerId);
    if (options.providerSubId != null) params = params.set('providerSubId', options.providerSubId);
    if (options.useCardToken != null) params = params.set('useCardToken', options.useCardToken);
    return firstValueFrom(this.api.post<any>('register/express/complete', request, params));
  }

  /**
   * Start an express checkout session to save a new card token.
   * POST api/cardtokens/express/init?providerId=&providerSubId=
   */
  initCardTokenExpress(providerId: ProviderName, providerSubId?: ProviderName): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('providerId', providerId);
    if (providerSubId != null) {
      params = params.set('providerSubId', providerSubId);
    }
    return firstValueFrom(this.api.post<any>('cardtokens/express/init', {}, params));
  }

  /**
   * Complete an express card token save with the provider callback data.
   * POST api/cardtokens/express/complete?orderId=&providerId=&providerSubId=
   */
  completeCardTokenExpress(
    orderId: string,
    providerId: ProviderName,
    callback: ExpressCheckoutCallback,
    providerSubId?: ProviderName
  ): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('orderId', orderId).set('providerId', providerId);
    if (providerSubId != null) {
      params = params.set('providerSubId', providerSubId);
    }
    return firstValueFrom(this.api.post<any>('cardtokens/express/complete', callback, params));
  }

  /**
   * Start an express checkout session for an order that already exists - the Checkout V4 flow.
   *
   * Validates the basket, resolves the wallet provider, and returns the SDK credentials and order
   * amount needed to load the wallet UI.
   *
   * Sends no body: the server already holds the basket against `orderId`, so the order id is the
   * whole request. That is the difference from the register/express init below, which has no order
   * yet and posts its basket lines instead.
   *
   * POST api/checkout/express/init?orderId=&providerId=&saveCardToken=&providerSubId=
   * 400 when the basket is empty, 404 when the order id is not found.
   */
  initCheckoutExpress(
    orderId: string,
    providerId: ProviderName,
    options: { providerSubId?: ProviderName; saveCardToken?: boolean } = {}
  ): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('orderId', orderId).set('providerId', providerId);
    if (options.saveCardToken != null) params = params.set('saveCardToken', options.saveCardToken);
    if (options.providerSubId != null) params = params.set('providerSubId', options.providerSubId);
    // null rather than {}: the endpoint takes no body, and an empty object would have the server
    // parse a basket payload that is not there.
    return firstValueFrom(this.api.post<any>('checkout/express/init', null, params));
  }

  /**
   * Complete an express checkout session - the Checkout V4 flow.
   *
   * The server decodes the wallet SDK's JWS response, decrypts the secured payload, takes the
   * payment and updates the basket status. `callback.responseString` must be the JWS string the
   * provider's complete callback handed back: it is the payment credential, so the call cannot
   * succeed without it.
   *
   * saveCardToken has to match what initCheckoutExpress was called with, per the spec - the two
   * together decide whether the order is charged once or set up as a subscription.
   *
   * POST api/checkout/express/complete?orderId=&providerId=&saveCardToken=&providerSubId=
   */
  completeCheckoutExpress(
    orderId: string,
    providerId: ProviderName,
    callback: ExpressCheckoutCallback,
    options: { providerSubId?: ProviderName; saveCardToken?: boolean } = {}
  ): Promise<ProviderUiActionResponse> {
    let params = new HttpParams().set('orderId', orderId).set('providerId', providerId);
    if (options.saveCardToken != null) params = params.set('saveCardToken', options.saveCardToken);
    if (options.providerSubId != null) params = params.set('providerSubId', options.providerSubId);
    return firstValueFrom(this.api.post<ExpressCheckoutCallback>('checkout/express/complete', callback, params));
  }

  /**
   * Complete an express checkout session, routing to the card-token or marketplace complete endpoint
   * based on `context.mode`. Mirrors `initExpress` so callers pass one context + callback and let the
   * service pick the endpoint. Card-token complete needs no basket items; marketplace supplies them.
   */
  completeExpress(
    context: ExpressCheckoutContext,
    providerId: ProviderName,
    callback: ExpressCheckoutCallback,
    providerSubId?: ProviderName
  ): Promise<ProviderUiActionResponse> {
    switch (context?.mode) {
      case 'marketplace':
        return this.completeCheckoutExpress(context.orderId, providerId, callback, {
          providerSubId,
          saveCardToken: context.saveCardToken,
        });
      case 'registration':
        return this.completeRegistrationExpress(
          context.orderId,
          providerId,
          { giftCodes: context.giftCodes ?? [], callback },
          { providerSubId, useCardToken: context.useCardToken }
        );
      default:
        return this.completeCardTokenExpress(context.orderId, providerId, callback, providerSubId);
    }
  }

  /**
   * Load an external script by url. Idempotent: an already-loaded url resolves immediately, and
   * concurrent calls for the same url share one request. Resolves with the <script> element.
   */
  loadScript(src: string, options: LoadScriptOptions = {}): Promise<HTMLScriptElement> {
    if (!src) {
      return Promise.reject(new Error('ExpressCheckout: cannot load script with an empty url'));
    }

    const existing = this.scripts.get(src);
    if (existing) {
      return existing.promise;
    }

    // Adopt a matching tag that was already added to the DOM (e.g. by another instance).
    const preexisting = this.findExistingTag(src);
    if (preexisting) {
      this.logService.info('ExpressCheckout: adopting existing script tag', src);
      const record: ScriptRecord = { src, el: preexisting, loaded: true, promise: Promise.resolve(preexisting) };
      this.scripts.set(src, record);
      return record.promise;
    }

    const el = this.createTag(src, options);
    const promise = this.awaitLoad(src, el, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

    this.scripts.set(src, { src, el, loaded: false, promise });
    this.document.head.appendChild(el);
    this.logService.info('ExpressCheckout: loading script', src);

    return promise;
  }

  /** Whether a script url has finished loading. */
  isLoaded(src: string): boolean {
    return this.scripts.get(src)?.loaded ?? false;
  }

  /** Remove a previously loaded script tag and forget it, so it can be re-loaded if needed. */
  unloadScript(src: string): void {
    const record = this.scripts.get(src);
    record?.el.parentNode?.removeChild(record.el);
    this.scripts.delete(src);
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private createTag(src: string, options: LoadScriptOptions): HTMLScriptElement {
    const el = this.document.createElement('script');
    el.type = 'text/javascript';
    el.src = src;
    el.async = options.async ?? true;
    el.defer = options.defer ?? false;
    if (options.crossOrigin) {
      el.crossOrigin = options.crossOrigin;
    }
    if (options.nonce) {
      el.setAttribute('nonce', options.nonce);
    }
    return el;
  }

  private awaitLoad(src: string, el: HTMLScriptElement, timeoutMs: number): Promise<HTMLScriptElement> {
    return new Promise<HTMLScriptElement>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;

      const cleanup = () => {
        el.onload = null;
        el.onerror = null;
        if (timer) clearTimeout(timer);
      };

      const fail = (message: string) => {
        cleanup();
        this.scripts.delete(src);
        el.parentNode?.removeChild(el);
        this.logService.error(`ExpressCheckout: ${message}`, src);
        reject(new Error(`${message}: ${src}`));
      };

      el.onload = () => {
        cleanup();
        const record = this.scripts.get(src);
        if (record) record.loaded = true;
        this.logService.info('ExpressCheckout: script loaded', src);
        resolve(el);
      };

      el.onerror = () => fail('script failed to load');

      if (timeoutMs > 0) {
        timer = setTimeout(() => fail(`script load timed out after ${timeoutMs}ms`), timeoutMs);
      }
    });
  }

  private findExistingTag(src: string): HTMLScriptElement | null {
    const tags = Array.from(this.document.getElementsByTagName('script'));
    return tags.find((tag) => tag.src === src) ?? null;
  }
}
